import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import "./i18n";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { initializeAnalyticsConsentListener } from "./lib/analytics";
import { redactBearerParamsDeep } from "./lib/sentryRedaction";

initializeAnalyticsConsentListener();

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.NODE_ENV ||
      "development",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    tracesSampleRate: 0.05,
    // Keep unsubscribe and invite tokens out of error reports. See
    // lib/sentryRedaction.ts.
    beforeSend: (event) => redactBearerParamsDeep(event),
    beforeSendTransaction: (event) => redactBearerParamsDeep(event),
    beforeBreadcrumb: (breadcrumb) => redactBearerParamsDeep(breadcrumb),
  });
}

// Remove the static <meta name="description">/<link rel="canonical">
// placeholders from public/index.html before Helmet mounts, so JS clients
// (real browsers, and any crawler that executes our JS) never end up with
// both the static default and the per-page Seo.tsx value at once. Clients
// that never run this script keep the static defaults untouched. This
// intentionally excludes <title>: react-helmet-async updates document.title
// in place rather than inserting a second <title> element, so there is no
// duplication risk there, and stripping it would leave routes that never
// render <Seo> (e.g. authenticated dashboard pages) with a blank tab title.
document
  .querySelectorAll('[data-default="true"]')
  .forEach((el) => el.remove());

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
